/**
 * Reordering, as pure functions so it unit-tests without a DOM.
 *
 * The same precedent as `lib/twoColumn.ts` and `lib/orgTree.ts`: the logic that is easy
 * to get subtly wrong lives here, and the component above it only has to wire events.
 */

/**
 * Moves the item at `from` to `to`, returning a NEW array.
 *
 * Out-of-range indices and `from === to` return the input unchanged rather than
 * throwing — a reorder handler firing with a stale index during a re-render is normal,
 * and a no-op is the right answer to it.
 */
export const moveItem = <T>(items: readonly T[], from: number, to: number): T[] => {
  if (from === to) return [...items];
  if (from < 0 || from >= items.length) return [...items];
  if (to < 0 || to >= items.length) return [...items];

  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...items];
  next.splice(to, 0, moved);
  return next;
};

/** Move to position 0 or to the end. The row overflow's shortcut for a long list. */
export const moveToEnd = <T>(items: readonly T[], from: number, edge: 'start' | 'end'): T[] =>
  moveItem(items, from, edge === 'start' ? 0 : items.length - 1);

/**
 * Stamps `displayOrder` from the array index, ONCE, at submit time.
 *
 * ⚠️ THE ARRAY ORDER IS THE ORDER. Carrying a `displayOrder` on each item while it sits
 * in an array would be two sources of truth, and they diverge the first time a row is
 * removed — see `policy.schema.ts`, where the server refuses to accept one on input for
 * the same reason.
 */
export const withDisplayOrder = <T>(items: readonly T[]): (T & { displayOrder: number })[] =>
  items.map((item, index) => ({ ...item, displayOrder: index }));
